<script setup lang="ts">
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { AxiosError } from "axios";
import { storeToRefs } from "pinia";
import { en as primeVueEn } from "primelocale/en.json";
import { fr as primeVueFr } from "primelocale/fr.json";
import { computed, getCurrentInstance, onErrorCaptured, watch } from "vue";
import { useRoute } from "vue-router";

import { useLayout } from "@/composables/layout/use-layout";
import { useSentry } from "@/composables/sentry/use-sentry";
import { CRouteParamsError } from "@/composables/use-route-params/route-params-error.types";
import { EV4bClientErrorCategory } from "@/data/api/kili/common/kili-errors.type";
import { V4bApiError } from "@/data/api/v4b/error/v4b-client-error.types";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName, ROUTES_WITHOUT_SIDE_MENU } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useCurrentUserStore } from "@/stores/current-user/current-user-store";

import Toast from "primevue/toast";

import ContactVoltalisDialog from "@/components/contact/contact-voltalis-dialog.vue";
import SideMenu from "@/components/layout/side-menu.vue";
import GlobalLoader from "@/components/ui/loader/global-loader.vue";

const currentRoute = useRoute();
const { locale } = useTypedIntl();
const sentry = useSentry();
const { isGlobalLoading } = useLayout();

const appInstance = getCurrentInstance();

const currentUserStore = useCurrentUserStore();
const { currentUser } = storeToRefs(currentUserStore);

const isSideMenuVisible = computed(() => {
  return currentUser.value && !ROUTES_WITHOUT_SIDE_MENU.includes(currentRoute.name as ERouteName);
});

watch(locale, (nextLocale) => {
  if (!appInstance) {
    return;
  }

  let primevueLocale = primeVueFr;

  switch (nextLocale) {
    case "en":
      primevueLocale = primeVueEn;
      break;
    case "fr":
    default:
      primevueLocale = primeVueFr;
  }

  appInstance.appContext.config.globalProperties.$primevue.config.locale = primevueLocale;
});

watch(
  currentUser,
  async (newUser, oldUser) => {
    sentry.setUser(newUser);

    if (!newUser && !!oldUser) {
      // when user logs out
      return;
    }

    if (!newUser) {
      return;
    }

    if (newUser.userName === oldUser?.userName) {
      return;
    }

    MixpanelService.identify(newUser);

    if (currentRoute.name === ERouteName.LOGIN) {
      MixpanelService.trackEvent(EMixpanelEvent.USER_LOGGED_IN, {
        method: "manual",
      });
      return;
    }

    MixpanelService.trackEvent(EMixpanelEvent.USER_LOGGED_IN, {
      method: "auto",
    });
  },
  { immediate: true },
);

async function handleUnauthenticatedError() {
  isGlobalLoading.value = true;

  currentUserStore.logout();
  await router.push({ name: ERouteName.LOGIN });

  isGlobalLoading.value = false;
}

onErrorCaptured((error: Error) => {
  console.error(error);

  if (ROUTES_WITHOUT_SIDE_MENU.includes(currentRoute.name as ERouteName)) return false;

  const isAxios = error instanceof AxiosError;
  const status = isAxios ? error.response?.status : undefined;

  const maybeErrorCategory = (error as AxiosError<V4bApiError>).response?.data?.errorCategory;
  const isClientError = Object.values(EV4bClientErrorCategory).find((c) => c === maybeErrorCategory);
  const isRouteParamError = error instanceof CRouteParamsError;

  // do not capture 401, client errors, nor 403
  if (status === 401) {
    handleUnauthenticatedError();
  } else if (status === 403 || isRouteParamError) {
    const currentRouteParentIdx = currentRoute.matched.length - 2;
    const maybeParentRoute = currentRoute.matched[currentRouteParentIdx];

    if (maybeParentRoute) {
      router.replace({ name: maybeParentRoute.name });
    }
  } else if (!isClientError) {
    sentry.captureException(error);
  }

  return false;
});
</script>

<template>
  <GlobalLoader />

  <div class="w-full h-full flex">
    <SideMenu v-if="isSideMenuVisible" />

    <div class="h-full w-full flex flex-col overflow-auto">
      <router-view />
    </div>
  </div>

  <ContactVoltalisDialog
    v-if="isSideMenuVisible"
    id="contact-voltalis-dialog"
    class="!z-1000"
  />

  <Toast position="bottom-right" />

  <VueQueryDevtools />
</template>
